import { FunctionEntry } from '@/types/simulation';

export interface ImportResult {
  functions: Omit<FunctionEntry, 'id'>[];
  errors: string[];
  rawText?: string;
}

const PRESET_COLORS = [
  '#00d9ff', '#ff6b35', '#ffd700', '#ff4da6', '#7fff00', '#c084fc', '#ff8c00',
];

function pickColor(index: number): string {
  return PRESET_COLORS[index % PRESET_COLORS.length];
}

// ─── 数式行の判定 ────────────────────────────────────────────────

/** 数式として有効かチェック（xを含む数学的な式） */
function isMathExpression(s: string): boolean {
  // xを含み、数学的な演算子・関数を含む
  return /[x]/.test(s) && /[\+\-\*\/\^()]|sin|cos|tan|exp|log|sqrt|abs|pow/.test(s);
}

/** 数値かどうか判定 */
function isNumeric(s: string): boolean {
  return /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s.trim());
}

/** 行が「情報テキスト」（説明文・タイトル・ラベルなど）かどうか判定 */
function isInfoLine(line: string): boolean {
  // コメント行
  if (line.startsWith('#') || line.startsWith('//') || line.startsWith('%')) return true;
  // 日本語・中国語・韓国語を含む行（説明文の可能性が高い）
  if (/[\u3000-\u9FFF\uAC00-\uD7AF]/.test(line)) return true;
  // 純粋なテキスト（数字・演算子・英字関数名を含まない）
  if (!/[\d\+\-\*\/\^=\(\)]/.test(line) && !/sin|cos|tan|exp|log|sqrt|abs/.test(line)) return true;
  // URLやパス
  if (/^https?:\/\/|^\/|^\.\//.test(line)) return true;
  return false;
}

// ─── 数式抽出 ────────────────────────────────────────────────────

/** テキストから数式のみを抽出（説明文・ラベル・コメントを無視） */
function extractExpressionsFromText(text: string): string[] {
  const lines = text.split(/\r?\n/);
  const expressions: string[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    if (isInfoLine(line)) continue;

    // y = ... / f(x) = ... 形式
    const yEqMatch = line.match(/^(?:y\s*=|f\s*\(x\)\s*=|g\s*\(x\)\s*=|h\s*\(x\)\s*=)\s*(.+)$/i);
    if (yEqMatch) {
      const expr = yEqMatch[1].trim();
      if (expr) { expressions.push(expr); continue; }
    }

    // CSV行: カンマ区切りで最初の列が数式
    if (line.includes(',')) {
      const parts = line.split(',').map((p) => p.trim());
      // ヘッダー行スキップ（x, y などのラベル）
      if (/^(x|y|t|formula|expression|式|関数|value|data)$/i.test(parts[0])) continue;
      // 最初の列が数式
      if (parts[0] && isMathExpression(parts[0])) {
        expressions.push(parts[0]); continue;
      }
      continue;
    }

    // タブ区切り行: 数式列を探す
    if (line.includes('\t')) {
      const parts = line.split('\t').map((p) => p.trim());
      for (const part of parts) {
        if (isMathExpression(part)) { expressions.push(part); break; }
      }
      continue;
    }

    // 純粋な数式行（xを含む）
    if (isMathExpression(line)) {
      expressions.push(line);
    }
  }

  return [...new Set(expressions)];
}

// ─── 数値表（x,y データ）の抽出 ──────────────────────────────────

interface TableData {
  xValues: number[];
  yValues: number[];
  label?: string;
}

/**
 * テキストから数値表（x列, y列）を抽出する。
 * グラフの後ろにある数値データテーブルを読み込む。
 *
 * 対応形式:
 * - CSV: x,y 形式
 * - TSV: x\ty 形式
 * - スペース区切り: x y 形式
 * - 単一列（y値のみ）: 等間隔xとして扱う
 */
function extractTableData(text: string): TableData[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const tables: TableData[] = [];

  // 区切り文字を自動検出
  const detectDelimiter = (line: string): string | null => {
    if (line.includes('\t')) return '\t';
    if (line.includes(',')) return ',';
    if (/\s{2,}/.test(line)) return /\s+/;
    if (line.includes(';')) return ';';
    return null;
  };

  // 連続する数値行のブロックを検出
  let currentBlock: string[] = [];
  let currentDelimiter: string | RegExp | null = null;
  let headerLabel: string | undefined;

  const flushBlock = () => {
    if (currentBlock.length < 2) { currentBlock = []; return; }

    const xValues: number[] = [];
    const yValues: number[] = [];

    for (const line of currentBlock) {
      const delim = currentDelimiter ?? /\s+/;
      const parts = typeof delim === 'string'
        ? line.split(delim).map((p) => p.trim())
        : line.split(delim).map((p) => p.trim());

      const nums = parts.filter(isNumeric).map(Number);
      if (nums.length >= 2) {
        xValues.push(nums[0]);
        yValues.push(nums[1]);
      } else if (nums.length === 1) {
        // 単一列: インデックスをxとして使用
        xValues.push(xValues.length);
        yValues.push(nums[0]);
      }
    }

    if (xValues.length >= 3) {
      tables.push({ xValues, yValues, label: headerLabel });
    }
    currentBlock = [];
    currentDelimiter = null;
    headerLabel = undefined;
  };

  for (const line of lines) {
    if (isInfoLine(line)) {
      flushBlock();
      // ヘッダーラベルとして保存
      if (line.startsWith('#') || line.startsWith('//')) {
        headerLabel = line.replace(/^[#/\s]+/, '').trim();
      }
      continue;
    }

    // ヘッダー行（x, y などのラベル）をスキップ
    const lowerLine = line.toLowerCase();
    if (/^(x|y|t|time|value|data|index)[\s,;\t]/.test(lowerLine)) {
      headerLabel = line;
      continue;
    }

    // 数値行かチェック
    const delim = detectDelimiter(line);
    const parts = delim
      ? (typeof delim === 'string' ? line.split(delim) : line.split(delim)).map((p) => p.trim())
      : [line];
    const numericParts = parts.filter(isNumeric);

    if (numericParts.length >= 1) {
      if (currentBlock.length === 0) currentDelimiter = delim;
      currentBlock.push(line);
    } else {
      flushBlock();
    }
  }
  flushBlock();

  return tables;
}

/** 数値表から多項式フィットして数式を生成 */
function tableToExpression(table: TableData): string | null {
  const { xValues, yValues } = table;
  if (xValues.length < 3) return null;

  const points = xValues.map((x, i) => ({ x, y: yValues[i] }));

  // 最小二乗法で多項式フィット（1〜4次）
  let bestFormula = '';
  let bestR2 = 0;

  for (const deg of [1, 2, 3, 4]) {
    const { coeffs, r2 } = fitPolynomialSimple(points, deg);
    if (r2 > bestR2 && coeffs.length > 0) {
      bestR2 = r2;
      bestFormula = coeffsToFormula(coeffs);
    }
  }

  return bestFormula || null;
}

function fitPolynomialSimple(
  points: { x: number; y: number }[],
  degree: number,
): { coeffs: number[]; r2: number } {
  const n = points.length;
  if (n < degree + 1) return { coeffs: [], r2: 0 };

  const A: number[][] = points.map(({ x }) =>
    Array.from({ length: degree + 1 }, (_, k) => Math.pow(x, k))
  );
  const b = points.map(({ y }) => y);

  const AT = A[0].map((_, j) => A.map((row) => row[j]));
  const ATA = AT.map((row) => AT[0].map((_, j) => row.reduce((s, v, k) => s + v * AT[j][k], 0)));
  const ATb = AT.map((row) => row.reduce((s, v, k) => s + v * b[k], 0));

  const coeffs = gaussElim(ATA, ATb);
  if (!coeffs) return { coeffs: [], r2: 0 };

  const yMean = b.reduce((s, v) => s + v, 0) / n;
  const ssTot = b.reduce((s, v) => s + (v - yMean) ** 2, 0);
  const ssRes = points.reduce((s, { x, y }) => {
    const yHat = coeffs.reduce((sum, c, k) => sum + c * Math.pow(x, k), 0);
    return s + (y - yHat) ** 2;
  }, 0);
  return { coeffs, r2: ssTot === 0 ? 1 : Math.max(0, 1 - ssRes / ssTot) };
}

function gaussElim(A: number[][], b: number[]): number[] | null {
  const n = A.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let col = 0; col < n; col++) {
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) maxRow = row;
    }
    [M[col], M[maxRow]] = [M[maxRow], M[col]];
    if (Math.abs(M[col][col]) < 1e-12) return null;
    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / M[col][col];
      for (let k = col; k <= n; k++) M[row][k] -= factor * M[col][k];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = M[i][n];
    for (let j = i + 1; j < n; j++) x[i] -= M[i][j] * x[j];
    x[i] /= M[i][i];
  }
  return x;
}

function coeffsToFormula(coeffs: number[]): string {
  const round = (v: number) => Math.round(v * 10000) / 10000;
  const terms: string[] = [];
  for (let k = coeffs.length - 1; k >= 0; k--) {
    const c = round(coeffs[k]);
    if (Math.abs(c) < 1e-6) continue;
    if (k === 0) terms.push(`${c}`);
    else if (k === 1) {
      if (Math.abs(c - 1) < 1e-6) terms.push('x');
      else if (Math.abs(c + 1) < 1e-6) terms.push('-x');
      else terms.push(`${c}*x`);
    } else {
      if (Math.abs(c - 1) < 1e-6) terms.push(`x^${k}`);
      else if (Math.abs(c + 1) < 1e-6) terms.push(`-x^${k}`);
      else terms.push(`${c}*x^${k}`);
    }
  }
  return terms.length ? terms.join(' + ').replace(/\+ -/g, '- ') : '0';
}

// ─── JSON形式のインポート ─────────────────────────────────────────

function importFromJSON(text: string): ImportResult {
  try {
    const data = JSON.parse(text);
    const errors: string[] = [];
    const functions: Omit<FunctionEntry, 'id'>[] = [];

    if (data.functions && Array.isArray(data.functions)) {
      data.functions.forEach((f: Partial<FunctionEntry>, i: number) => {
        if (typeof f.expression === 'string') {
          functions.push({
            expression: f.expression,
            color: f.color ?? pickColor(i),
            visible: f.visible ?? true,
          });
        }
      });
      return { functions, errors };
    }

    if (Array.isArray(data)) {
      data.forEach((item: unknown, i: number) => {
        if (typeof item === 'string') {
          functions.push({ expression: item, color: pickColor(i), visible: true });
        } else if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          const expr = (obj.expression ?? obj.formula ?? obj.expr ?? obj.y) as string | undefined;
          if (typeof expr === 'string') {
            functions.push({
              expression: expr,
              color: (obj.color as string) ?? pickColor(i),
              visible: true,
            });
          }
        }
      });
      return { functions, errors };
    }

    errors.push('JSONの形式が認識できませんでした。');
    return { functions, errors };
  } catch {
    return { functions: [], errors: ['JSONの解析に失敗しました。'] };
  }
}

// ─── テキスト/CSVのインポート ─────────────────────────────────────

/**
 * テキスト/CSVから数式と数値表を読み込む。
 *
 * 優先順位:
 * 1. y = f(x) 形式の数式行 → そのまま数式として読み込む
 * 2. x,y 数値表 → 多項式フィットして数式に変換
 * 3. 説明文・ラベル・コメント → 完全に無視
 */
function importFromText(text: string): ImportResult {
  const functions: Omit<FunctionEntry, 'id'>[] = [];
  const errors: string[] = [];
  let colorIdx = 0;

  // 1. 数式行を抽出（y = sin(x) など）
  const expressions = extractExpressionsFromText(text);
  for (const expr of expressions) {
    functions.push({ expression: expr, color: pickColor(colorIdx++), visible: true });
  }

  // 2. 数値表を抽出してフィット
  const tables = extractTableData(text);
  for (const table of tables) {
    const expr = tableToExpression(table);
    if (expr) {
      functions.push({ expression: expr, color: pickColor(colorIdx++), visible: true });
    }
  }

  if (functions.length === 0) {
    errors.push(
      '数式・数値データが見つかりませんでした。\n' +
      '対応形式: y = sin(x) 形式の数式、または x,y の数値表（CSV/TSV）'
    );
  }

  return { functions, errors, rawText: text };
}

// ─── 公開API ─────────────────────────────────────────────────────

/** 画像からグラフデータを読み込む（画像をオーバーレイとして返す） */
export async function importFromImage(file: File): Promise<{ dataUrl: string; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      const img = new Image();
      img.onload = () => resolve({ dataUrl, width: img.width, height: img.height });
      img.onerror = () => reject(new Error('画像の読み込みに失敗しました。'));
      img.src = dataUrl;
    };
    reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました。'));
    reader.readAsDataURL(file);
  });
}

/** メインのインポート関数 */
export async function importFromFile(file: File): Promise<ImportResult> {
  const name = file.name.toLowerCase();
  const text = await file.text();

  if (name.endsWith('.json')) return importFromJSON(text);
  return importFromText(text);
}

/** kuregraph JSON形式でエクスポート */
export function exportToJSON(functions: FunctionEntry[]): string {
  return JSON.stringify({ version: 1, functions }, null, 2);
}
